"use client";

import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium inline-block">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <footer id="footer" className="dark bg-background text-foreground border-t border-border/10">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {}
          <div className="flex flex-col gap-6">
            <Link to="/" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/shark_bite_land_clearing_logo.png" alt="Shark Bite Land Clearing" className="h-12 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Shark Bite Land Clearing is a rugged, modern land‑clearing firm operating throughout East Texas. 
              We deliver efficient forestry mulching, brush removal, and property cleanup with precision and power.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="border-[var(--chart-3)] text-[var(--chart-3)] bg-[var(--chart-3)]/10 px-3 py-1 text-xs uppercase tracking-wider font-bold gap-1.5">
                <ShieldCheck className="size-3.5" />
                Fully Insured
              </Badge>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-serif font-bold tracking-tight text-foreground uppercase">
              Quick Links
            </h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenu />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-serif font-bold tracking-tight text-foreground uppercase">
              Contact Us
            </h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3 group">
                <div className="p-2 rounded-md bg-secondary text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Phone className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Call Us</span>
                  <Link to="tel:9034904941" className="text-foreground font-semibold hover:text-primary transition-colors">
                    903 490 4941
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <div className="p-2 rounded-md bg-secondary text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Mail className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Email Us</span>
                  <Link to="mailto:sharkbitetx@gmail.com" className="text-foreground font-semibold hover:text-primary transition-colors break-all">
                    sharkbitetx@gmail.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <div className="p-2 rounded-md bg-secondary text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Clock className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Hours</span>
                  <span className="text-foreground text-sm">Mon - Fri: 7am - 6pm</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-serif font-bold tracking-tight text-foreground uppercase">
              Service Area
            </h3>
            <div className="flex flex-col gap-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Proudly serving landowners, ranchers, and property developers across East Texas.
              </p>
              <div className="flex items-start gap-3">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-foreground">East Texas Region</span>
                  <span className="text-xs text-muted-foreground">Texarkana, Longview, Tyler, Marshall, and surrounding counties.</span>
                </div>
              </div>
              <div className="mt-2">
                <Button asChild variant="outline" size="sm" className="w-full border-primary/30 hover:border-primary hover:bg-primary/10 text-primary hover:text-primary">
                  <Link to="/contact">Get a Free Estimate</Link>
                </Button>
              </div>
            </div>
          </div>

        </div>

        <Separator className="my-12 bg-border/10" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} Shark Bite Land Clearing. All rights reserved.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Link to="#" className="p-2 rounded-full bg-secondary text-muted-foreground hover:bg-primary hover:text-white transition-all duration-300" aria-label="Facebook">
              <Facebook className="size-4" />
            </Link>
            <Link to="#" className="p-2 rounded-full bg-secondary text-muted-foreground hover:bg-primary hover:text-white transition-all duration-300" aria-label="Instagram">
              <Instagram className="size-4" />
            </Link>
            <Link to="#" className="p-2 rounded-full bg-secondary text-muted-foreground hover:bg-primary hover:text-white transition-all duration-300" aria-label="YouTube">
              <Youtube className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    </footer>;
}